import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  MetricasApi,
  MetricasDesempenhoApi,
  ColaboradoresApi,
  ConfigApi,
  PenalidadesApi,
} from "../api/client.js";
import { useMemo, useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { Table } from "../components/Table.jsx";
import {
  ResponsiveContainer,
  XAxis,
  YAxis,
  Tooltip,
  Legend,
  BarChart,
  Bar,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import html2canvas from "html2canvas";
import { useToast } from "../components/ToastProvider.jsx";
import { useAuth } from "../context/AuthContext.jsx";
import { Modal } from "../components/Modal.jsx";
import { useDefaultColaborador } from "../hooks/useDefaultColaborador.js";
import { useIsMobile } from "../hooks/useIsMobile.js";
import { formatDate } from "../utils/formatData.js";

export default function MetricasPage() {
  function countBusinessDaysInMonth(year, month) {
    const first = new Date(year, month - 1, 1);
    const last = new Date(year, month, 0);
    let count = 0;
    for (let d = new Date(first); d <= last; d.setDate(d.getDate() + 1)) {
      const day = d.getDay();
      if (day !== 0 && day !== 6) count += 1;
    }
    return count;
  }

  function round2(v) {
    return Math.round((Number(v) + Number.EPSILON) * 100) / 100;
  }

  function getMonthRangeFromISO(dateISO, fallbackYear, fallbackMonth) {
    const [yearRaw, monthRaw] = String(dateISO || "").split("-").map(Number);
    const year = Number.isFinite(yearRaw) ? yearRaw : fallbackYear;
    const month = Number.isFinite(monthRaw) ? monthRaw : fallbackMonth;
    const monthPadded = String(month).padStart(2, "0");
    const lastDay = new Date(year, month, 0).getDate();
    return {
      year,
      month,
      monthKey: `${year}-${monthPadded}`,
      inicio: `${year}-${monthPadded}-01`,
      fim: `${year}-${monthPadded}-${String(lastDay).padStart(2, "0")}`,
    };
  }

  const qc = useQueryClient();
  const { data: config } = useQuery({
    queryKey: ["config"],
    queryFn: () => ConfigApi.obter(),
  });
  const { data: penalidades } = useQuery({
    queryKey: ["penalidades"],
    queryFn: () => PenalidadesApi.listar(),
  });
  const penalidadesLista = Array.isArray(penalidades) ? penalidades : [];
  const { perfil, auth } = useAuth();
  const navigate = useNavigate();
  const { colaboradorId, setColaboradorId } = useDefaultColaborador();
  const hoje = new Date();
  const anoAtual = hoje.getFullYear();
  const mesAtual = hoje.getMonth() + 1;
  const diaHoje = String(hoje.getDate()).padStart(2, "0");
  const hojeISO = `${anoAtual}-${String(mesAtual).padStart(2, "0")}-${diaHoje}`;
  const perfilNormalizado = String(perfil || "")
    .trim()
    .toUpperCase();
  const isAuthReady = Boolean(perfilNormalizado);
  const isColaborador = perfilNormalizado === "COLABORADOR";
  const podeEditarMetricas = ["LIDER", "RH", "GERENTE"].includes(perfilNormalizado);
  const podeEscolherData = ["LIDER", "RH", "GERENTE"].includes(perfilNormalizado);
  const podeGerenciarDesempenho = ["RH", "GERENTE"].includes(perfilNormalizado);
  const { data: colaboradores } = useQuery({
    enabled: isAuthReady && !isColaborador,
    queryKey: ["colaboradores"],
    queryFn: () => ColaboradoresApi.listar(),
  });
  const { data: colaboradorMeResp } = useQuery({
    enabled: isAuthReady && isColaborador,
    queryKey: ["colaborador-me"],
    queryFn: () => ColaboradoresApi.me(),
  });
  const [inicio, setInicio] = useState(
    `${anoAtual}-${String(mesAtual).padStart(2, "0")}-01`,
  );
  // Fim agora inicia sempre com a data de hoje (em vez de placeholder dia 31)
  const [fim, setFim] = useState(
    `${anoAtual}-${String(mesAtual).padStart(2, "0")}-${diaHoje}`,
  );
  const [page, setPage] = useState(1);
  const pageSize = 100;
  const [dataMetrica, setDataMetrica] = useState(hojeISO);
  const dataReferenciaCalculo = podeEscolherData ? dataMetrica : hojeISO;
  const {
    year: anoRefCalculo,
    month: mesRefCalculo,
    monthKey: mesRefCalculoKey,
    inicio: inicioMesRefCalculo,
    fim: fimMesRefCalculo,
  } = getMonthRangeFromISO(dataReferenciaCalculo, anoAtual, mesAtual);
  const listaColaboradores = colaboradores?.data || colaboradores || [];
  const colaboradorIdFromAuth =
    auth?.usuario?.colaboradorId || auth?.usuario?.colaborador_id || "";
  const colaboradorIdEfetivo = isColaborador
    ? colaboradorId || colaboradorIdFromAuth
    : colaboradorId;
  const colaboradoresSelect = isColaborador
    ? colaboradorIdEfetivo
      ? [
          {
            id: colaboradorIdEfetivo,
            usuario: {
              nome: auth?.usuario?.nome,
              email: auth?.usuario?.email,
            },
          },
        ]
      : []
    : listaColaboradores;
  const {
    data: metricasResp,
    refetch,
    isFetching,
  } = useQuery({
    enabled: !!colaboradorIdEfetivo,
    queryKey: ["metricas", colaboradorIdEfetivo, inicio, fim, page],
    queryFn: () =>
      MetricasApi.listar({
        colaboradorId: colaboradorIdEfetivo,
        inicio,
        fim,
        page,
        pageSize,
      }),
  });

  const { data: metricasMesCalculoResp } = useQuery({
    enabled: !!colaboradorIdEfetivo,
    queryKey: [
      "metricas-dias-uteis-mes-calculo",
      colaboradorIdEfetivo,
      mesRefCalculoKey,
    ],
    queryFn: () =>
      MetricasApi.listar({
        colaboradorId: colaboradorIdEfetivo,
        inicio: inicioMesRefCalculo,
        fim: fimMesRefCalculo,
        page: 1,
        pageSize: 1,
      }),
  });

  const metricas = metricasResp?.data || [];
  const diasUteisPorMesApi = {
    ...(metricasResp?.meta?.diasUteisPorMes || {}),
    ...(metricasMesCalculoResp?.meta?.diasUteisPorMes || {}),
  };
  const totaisTabela = useMemo(() => {
    const somarCampo = (campo) =>
      metricas.reduce((acc, item) => {
        const valor = Number(item?.[campo]);
        return acc + (Number.isFinite(valor) ? valor : 0);
      }, 0);
    return {
      penalidade: somarCampo("valor_penalidade"),
      total: somarCampo("valor_total"),
    };
  }, [metricas]);
  const totalRegistros = metricasResp?.meta?.total ?? metricas.length;
  const totalPaginas = Number(metricasResp?.meta?.pages || 1);
  const inicioExibicao = totalRegistros ? (page - 1) * pageSize + 1 : 0;
  const fimExibicao = totalRegistros
    ? Math.min(page * pageSize, totalRegistros)
    : 0;

  const getDiasUteisMes = (ano, mes) => {
    const mesKey = `${ano}-${String(mes).padStart(2, "0")}`;
    const diasUteisApi = Number(diasUteisPorMesApi?.[mesKey]?.diasUteis);
    if (Number.isFinite(diasUteisApi) && diasUteisApi > 0) {
      return diasUteisApi;
    }
    return countBusinessDaysInMonth(ano, mes);
  };

  const graficoData = useMemo(
    () =>
      metricas.map((m) => ({
        data: m.data.slice(5),
        fullDate: m.data,
        m1: m.m1,
        m2: m.m2,
        m3: m.m3,
        m4: m.m4,
        total: m.total_pontos,
        valor: Number(m.valor_diario),
      })),
    [metricas],
  );

  // Resumo e agregações para gráficos no estilo do módulo Relatórios
  const resumo = useMemo(() => {
    if (!metricas.length) return null;
    const dias = metricas.length;
    let somaValor = 0,
      somaPontos = 0,
      somaPenalidades = 0,
      valorLiquidoTotal = 0,
      c1 = 0,
      c2 = 0,
      c3 = 0,
      c4 = 0;
    metricas.forEach((m) => {
      somaValor += Number(m.valor_diario);
      somaPontos += m.total_pontos;
      somaPenalidades += Number(m.valor_penalidade) || 0;
      valorLiquidoTotal += Number(m.valor_total) || 0;
      c1 += m.m1;
      c2 += m.m2;
      c3 += m.m3;
      c4 += m.m4;
    });
    return {
      dias,
      somaValor,
      somaPontos,
      somaPenalidades,
      valorLiquidoTotal,
      medias: { pontosDia: somaPontos / dias, valorDia: somaValor / dias },
      counts: { m1: c1, m2: c2, m3: c3, m4: c4 },
    };
  }, [metricas]);

  const pieData = useMemo(
    () =>
      resumo
        ? Object.entries(resumo.counts).map(([k, v]) => ({
            name: k.toUpperCase(),
            value: v,
          }))
        : [],
    [resumo],
  );

  // Paleta diferenciada (roxos/azuis/esverdeado) para não confundir com Relatórios
  const cores = ["#6F5AA8", "#4DA3D9", "#5CBFA6", "#F0A54B"];
  const coresBordas = ["#5d4797", "#3b8bbf", "#44a789", "#d89035"];

  const CustomTooltipStack = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const total = payload.reduce((a, p) => a + (p.value || 0), 0);
    return (
      <div
        style={{
          background: "#fff",
          border: "1px solid var(--color-border)",
          padding: ".5rem .65rem",
          fontSize: ".6rem",
          borderRadius: 6,
          boxShadow: "var(--shadow-md)",
        }}
      >
        <strong style={{ display: "block", marginBottom: ".3rem" }}>
          Dia {label}
        </strong>
        {payload.map((p) => (
          <div
            key={p.dataKey}
            style={{ display: "flex", gap: 4, alignItems: "center" }}
          >
            <span
              style={{
                width: 10,
                height: 10,
                background: p.fill,
                borderRadius: 2,
              }}
            />
            <span style={{ textTransform: "uppercase" }}>{p.dataKey}</span>
            <span style={{ marginLeft: "auto", fontWeight: 600 }}>
              {p.value}
            </span>
          </div>
        ))}
        <div
          style={{
            marginTop: ".35rem",
            borderTop: "1px solid #eee",
            paddingTop: ".25rem",
            fontWeight: 600,
          }}
        >
          Total: {total}
        </div>
      </div>
    );
  };

  const agrupadoMes = useMemo(() => {
    const map = new Map();
    graficoData.forEach((d) => {
      const key = d.fullDate.slice(0, 7); // YYYY-MM
      if (!map.has(key))
        map.set(key, { mes: key, dias: 0, somaValor: 0, somaTotal: 0 });
      const obj = map.get(key);
      obj.dias++;
      obj.somaValor += d.valor;
      obj.somaTotal += d.total;
    });
    return Array.from(map.values()).map((o) => ({
      mes: o.mes,
      mediaValor: o.dias ? o.somaValor / o.dias : 0,
      mediaTotal: o.dias ? o.somaTotal / o.dias : 0,
      somaValor: o.somaValor,
      somaTotal: o.somaTotal,
    }));
  }, [graficoData]);

  const exportar = async (id, nome) => {
    const el = document.getElementById(id);
    if (!el) return;
    const canvas = await html2canvas(el, { backgroundColor: "#fff", scale: 2 });
    const link = document.createElement("a");
    link.href = canvas.toDataURL("image/png");
    link.download = nome + ".png";
    link.click();
  };

  // Form para criar métrica do dia
  const [m1, setM1] = useState(false);
  const [m2, setM2] = useState(false);
  const [m3, setM3] = useState(false);
  const [m4, setM4] = useState(false);
  const [penalidadeSelecionada, setPenalidadeSelecionada] = useState(null);
  const [valorDiario, setValorDiario] = useState("");
  const [obs, setObs] = useState("");
  const colaboradorMe = colaboradorMeResp?.data || colaboradorMeResp || null;
  const colaboradorSelecionado = isColaborador
    ? colaboradorMe
    : listaColaboradores.find((c) => c.id === colaboradorIdEfetivo);
  const cargoSelecionado = colaboradorSelecionado?.cargo || "";
  const [hoveredMetricaTipo, setHoveredMetricaTipo] = useState(null);

  useEffect(() => {
    if (isColaborador) return;
    if (colaboradorId) return;
    if (!auth?.usuario?.id || !listaColaboradores.length) return;
    const match = listaColaboradores.find(
      (c) => c.usuario?.id === auth.usuario.id,
    );
    if (match) setColaboradorId(match.id);
  }, [
    isColaborador,
    colaboradorId,
    auth?.usuario?.id,
    listaColaboradores,
    setColaboradorId,
  ]);

  // Lista de Metricas de Desempenho
  const { data: metricasDesempenho } = useQuery({
    queryKey: ["metricas-desempenho", "todos"],
    queryFn: () => MetricasDesempenhoApi.listarTodas(),
  });
  const metricasDesempenhoLista = metricasDesempenho?.data || [];

  const normalizarTexto = (valor) =>
    String(valor ?? "")
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .trim()
      .toUpperCase();

  const obterDescricaoDesempenho = (tipo) => {
    if (!cargoSelecionado) return "";
    const cargoNormalizado = normalizarTexto(cargoSelecionado);
    const tipoNormalizado = normalizarTexto(tipo);
    return (
      metricasDesempenhoLista.find(
        (item) =>
          normalizarTexto(item.cargo) === cargoNormalizado &&
          normalizarTexto(item.type) === tipoNormalizado,
      )?.descricao || null
    );
  };
  const descricaoM1 = obterDescricaoDesempenho("M1");
  const descricaoM2 = obterDescricaoDesempenho("M2");
  const descricaoM3 = obterDescricaoDesempenho("M3");
  const descricaoM4 = obterDescricaoDesempenho("M4");
  const bonusMensalGlobal = Number(config?.bonus_mensal_global || 500);
  const bonusMensalColaborador = Number(
    colaboradorSelecionado?.bonus_mensal_padrao,
  );
  const bonusMensalBase =
    Number.isFinite(bonusMensalColaborador) && bonusMensalColaborador > 0
      ? bonusMensalColaborador
      : bonusMensalGlobal;
  const diasUteisMesAtual = useMemo(
    () => getDiasUteisMes(anoRefCalculo, mesRefCalculo),
    [anoRefCalculo, mesRefCalculo, diasUteisPorMesApi],
  );
  const valorDiarioCalculado = useMemo(() => {
    const totalPontos = [m1, m2, m3, m4].reduce(
      (acc, item) => acc + (item ? 1 : 0),
      0,
    );
    const valorDiarioTotal = bonusMensalBase / diasUteisMesAtual;
    return round2((totalPontos / 4) * valorDiarioTotal);
  }, [m1, m2, m3, m4, bonusMensalBase, diasUteisMesAtual]);

  const valorDiarioNumero = Number(valorDiario || 0);
  const valorPenalidade = penalidadeSelecionada ? Number(penalidadeSelecionada.value) : 0;
  const valorTotal = round2(valorDiarioNumero + valorPenalidade);

  useEffect(() => {
    setValorDiario(valorDiarioCalculado.toFixed(2));
  }, [valorDiarioCalculado]);

  const calcularValorDiarioPorData = (dataISO, valM1, valM2, valM3, valM4) => {
    const [ano, mes] = (dataISO || "").split("-").map(Number);
    const anoRef = Number.isFinite(ano) ? ano : anoAtual;
    const mesRef = Number.isFinite(mes) ? mes : mesAtual;
    const diasUteis = getDiasUteisMes(anoRef, mesRef);
    const totalPontos = [valM1, valM2, valM3, valM4].reduce(
      (acc, item) => acc + (Number(item) ? 1 : 0),
      0,
    );
    const valorDiarioTotal = bonusMensalBase / diasUteis;
    return round2((totalPontos / 4) * valorDiarioTotal);
  };

  const prepararLinhaEdicao = (row) => {
    const valorBase =
      row?.valor_diario === "" || row?.valor_diario === null
        ? calcularValorDiarioPorData(
            row?.data,
            row?.m1,
            row?.m2,
            row?.m3,
            row?.m4,
          )
        : Number(row.valor_diario);
    return {
      ...row,
      valor_diario: Number.isFinite(valorBase) ? valorBase.toFixed(2) : "0.00",
    };
  };

  const toast = useToast();
  const criar = useMutation({
    mutationFn: () =>
      MetricasApi.criar({
        colaboradorId: colaboradorIdEfetivo,
        name:
          colaboradorSelecionado?.usuario?.nome ||
          colaboradorSelecionado?.usuario?.email ||
          colaboradorSelecionado?.nome ||
          cargoSelecionado,
        data: podeEscolherData ? dataMetrica : undefined,
        m1,
        m2,
        m3,
        m4,
        penalidade: penalidadeSelecionada ? 1 : 0,
        valor_penalidade: penalidadeSelecionada ? Number(penalidadeSelecionada.value) : undefined,
        valor_diario:
          valorDiario === "" || valorDiario === null
            ? undefined
            : Number(valorDiario),
        observacoes: obs,
      }),
    onSuccess: () => {
      setM1(false);
      setM2(false);
      setM3(false);
      setM4(false);
      setPenalidadeSelecionada(null);
      setValorDiario("0.00");
      setObs("");
      setDataMetrica(hojeISO);
      qc.invalidateQueries({
        queryKey: ["metricas", colaboradorIdEfetivo, inicio, fim, page],
      });
      refetch();
      toast.success("Métrica registrada");
    },
    onError: (e) => {
      const message =
        e?.response?.data?.error?.message ||
        e?.response?.data?.message ||
        "Erro ao salvar";
      toast.error(message);
    },
  });

  // Edição
  const [editOpen, setEditOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editData, setEditData] = useState(hojeISO);

  // Modal Gerenciar Penalidades
  const [penModalOpen, setPenModalOpen] = useState(false);
  const [penEditing, setPenEditing] = useState(null); // null = criando novo
  const [penForm, setPenForm] = useState({ nivel: "", type: "", value: "" });
  const [penDeleteConfirm, setPenDeleteConfirm] = useState(null);

  const criarPenalidade = useMutation({
    mutationFn: (dados) => PenalidadesApi.criar(dados),
    onSuccess: () => {
      setPenForm({ nivel: "", type: "", value: "" });
      setPenEditing(null);
      qc.invalidateQueries({ queryKey: ["penalidades"] });
      toast.success("Penalidade criada");
    },
    onError: (e) => {
      const message = e?.response?.data?.error?.message || e?.response?.data?.message || "Erro ao criar";
      toast.error(message);
    },
  });

  const atualizarPenalidade = useMutation({
    mutationFn: ({ id, dados }) => PenalidadesApi.atualizar(id, dados),
    onSuccess: () => {
      setPenEditing(null);
      setPenForm({ nivel: "", type: "", value: "" });
      qc.invalidateQueries({ queryKey: ["penalidades"] });
      toast.success("Penalidade atualizada");
    },
    onError: (e) => {
      const message = e?.response?.data?.error?.message || e?.response?.data?.message || "Erro ao atualizar";
      toast.error(message);
    },
  });

  const deletarPenalidade = useMutation({
    mutationFn: (id) => PenalidadesApi.deletar(id),
    onSuccess: () => {
      setPenDeleteConfirm(null);
      qc.invalidateQueries({ queryKey: ["penalidades"] });
      toast.success("Penalidade removida");
    },
    onError: (e) => {
      const message = e?.response?.data?.error?.message || e?.response?.data?.message || "Erro ao deletar";
      toast.error(message);
    },
  });

  const editMutation = useMutation({
    mutationFn: ({ id, payload }) => MetricasApi.atualizar(id, payload),
    onSuccess: () => {
      toast.success("Atualizado");
      setEditOpen(false);
      setEditData(hojeISO);
      qc.invalidateQueries({
        queryKey: ["metricas", colaboradorIdEfetivo, inicio, fim, page],
      });
      refetch();
    },
    onError: (e) => {
      const message =
        e?.response?.data?.error?.message ||
        e?.response?.data?.message ||
        "Falha ao atualizar";
      toast.error(message);
    },
  });

  const metricasNomesMock = {
    m1: "Separacao de reciclaveis",
    m2: "Organizacao de materiais",
    m3: "Registro de entrada",
    m4: "Limpeza da area",
  };
  const metricasNomes = {
    m1: descricaoM1 || metricasNomesMock.m1,
    m2: descricaoM2 || metricasNomesMock.m2,
    m3: descricaoM3 || metricasNomesMock.m3,
    m4: descricaoM4 || metricasNomesMock.m4,
  };

  const matrizMetricas = useMemo(() => {
    const items = [...metricas].sort((a, b) =>
      String(a.data).localeCompare(String(b.data)),
    );
    const datas = items.map((item) => item.data);
    const linhas = {
      m1: [],
      m2: [],
      m3: [],
      m4: [],
    };
    const penalidades = [];
    const totais = [];
    items.forEach((item) => {
      const m1 = Number(item.m1) || 0;
      const m2 = Number(item.m2) || 0;
      const m3 = Number(item.m3) || 0;
      const m4 = Number(item.m4) || 0;
      linhas.m1.push(m1);
      linhas.m2.push(m2);
      linhas.m3.push(m3);
      linhas.m4.push(m4);
      penalidades.push(Number(item.valor_penalidade) || 0);
      const total = Number(item.total_pontos);
      totais.push(Number.isFinite(total) ? total : m1 + m2 + m3 + m4);
    });
    return { datas, linhas, penalidades, totais };
  }, [metricas]);

  const renderMetricaHeader = (sigla, nome) => (
    <div style={{ lineHeight: 1.1, textAlign: "center" }}>
      <div style={{ fontWeight: 600 }}>{sigla}</div>
      <div style={{ fontSize: ".6rem", opacity: 0.75 }}>{nome}</div>
    </div>
  );

  const columns = [
    {
      key: "name",
      header: "Nome",
      render: (v) => (
        <span style={{ display: "block", textAlign: "center" }}>
          {v || "--"}
        </span>
      ),
    },
    {
      key: "data",
      header: "Data",
      thStyle: { whiteSpace: "nowrap" },
      render: (v) => formatDate(v),
    },
    {
      key: "m1",
      header: renderMetricaHeader("M1", metricasNomes.m1),
      thStyle: { minWidth: 120 },
    },
    {
      key: "m2",
      header: renderMetricaHeader("M2", metricasNomes.m2),
      thStyle: { minWidth: 120 },
    },
    {
      key: "m3",
      header: renderMetricaHeader("M3", metricasNomes.m3),
      thStyle: { minWidth: 120 },
    },
    {
      key: "m4",
      header: renderMetricaHeader("M4", metricasNomes.m4),
      thStyle: { minWidth: 120 },
    },
    { key: "total_pontos", header: "Total" },
    {
      key: "valor_diario",
      header: "Valor Diário (R$)",
      render: (v) => Number(v).toFixed(2),
    },
    {
      key: "valor_penalidade",
      header: "Penalidade",
      render: (v) => {
        const valor = Number(v);
        const style =
          Number.isFinite(valor) && valor < 0 ? { color: "#dc2626" } : null;
        return (
          <span style={style}>
            {Number.isFinite(valor) ? valor.toFixed(2) : "0.00"}
          </span>
        );
      },
    },
    {
      key: "valor_total",
      header: "Valor Total (R$)",
      render: (v) => {
        const valor = Number(v);
        const style =
          Number.isFinite(valor) && valor > 0 ? { color: "#16a34a" } : null;
        return (
          <span style={style}>
            {Number.isFinite(valor) ? valor.toFixed(2) : "0.00"}
          </span>
        );
      },
    },
    {
      key: "_acao",
      header: "",
      render: (_, row) => (
        <button
          className="btn outline"
          disabled={!podeEditarMetricas}
          style={{ padding: ".25rem .5rem", fontSize: ".65rem" }}
          onClick={() => {
            setEditing(prepararLinhaEdicao(row));
            setEditData(row?.data || hojeISO);
            setEditOpen(true);
          }}
        >
          Editar
        </button>
      ),
    },
  ];

  const isMobile = useIsMobile();

  return (
    <div>
      <h2 className="page-title">Métricas Diárias</h2>
      <div className="card" style={{ marginBottom: "1rem" }}>
        <form
          onSubmit={(e) => {
            e.preventDefault();
            setPage(1);
            refetch();
          }}
          className="flex gap-sm"
          style={{ flexWrap: "wrap", alignItems: "flex-end" }}
        >
          <div style={{ minWidth: "200px" }}>
            <label>Colaborador</label>
            <select
              value={colaboradorIdEfetivo}
              onChange={(e) => setColaboradorId(e.target.value)}
              required
              disabled={isColaborador}
            >
              <option value="">Selecione...</option>
              {colaboradoresSelect.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.usuario?.nome || c.usuario?.email || c.id}
                </option>
              ))}
            </select>
          </div>
          <div>
            <label>Início</label>
            <input
              type="date"
              value={inicio}
              onChange={(e) => setInicio(e.target.value)}
            />
          </div>
          <div>
            <label>Fim</label>
            <input
              type="date"
              value={fim}
              onChange={(e) => setFim(e.target.value)}
            />
          </div>
          <button
            type="submit"
            className="btn"
            disabled={!colaboradorIdEfetivo || isFetching}
          >
            Filtrar
          </button>
          {podeGerenciarDesempenho && (
            <button
              type="button"
              className="btn outline"
              onClick={() => navigate("/metricas/gerenciar-metricas")}
              style={{ marginLeft: "auto" }}
            >
              Gerenciar metricas de desempenho
            </button>
          )}
          {podeGerenciarDesempenho && (
            <button
              type="button"
              className="btn outline"
              onClick={() => {
                setPenEditing(null);
                setPenForm({ nivel: "", type: "", value: "" });
                setPenDeleteConfirm(null);
                setPenModalOpen(true);
              }}
            >
              Gerenciar penalidades
            </button>
          )}
        </form>
      </div>

      {colaboradorId && !isColaborador && (
        <div className="card" style={{ marginBottom: "1rem" }}>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              criar.mutate();
            }}
            style={{ display: "flex", flexDirection: "column", gap: ".85rem" }}
          >
            <div
              className="flex gap-sm"
              style={{ flexWrap: "wrap", alignItems: "center" }}
            >
              <strong style={{ fontSize: ".8rem" }}>
                {podeEscolherData ? "Registrar Metrica:" : "Registrar Hoje:"}
              </strong>

              <label
                onMouseEnter={() => setHoveredMetricaTipo("M1")}
                onMouseLeave={() => setHoveredMetricaTipo(null)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: ".3rem",
                  fontSize: ".7rem",
                  position: "relative",
                }}
              >
                <input
                  type="checkbox"
                  checked={m1}
                  disabled={!descricaoM1}
                  onChange={(e) => setM1(e.target.checked)}
                />
                <span style={{ cursor: "help" }}>M1</span>
                {hoveredMetricaTipo === "M1" && descricaoM1 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 8px)",
                      left: 0,
                      zIndex: 25,
                      width: 260,
                      padding: ".5rem .6rem",
                      borderRadius: 8,
                      background: "rgba(15, 23, 42, 0.98)",
                      color: "#fff",
                      fontSize: ".65rem",
                      lineHeight: 1.35,
                      boxShadow: "var(--shadow-md)",
                      pointerEvents: "none",
                    }}
                  >
                    {descricaoM1}
                  </div>
                )}
              </label>
              <label
                onMouseEnter={() => setHoveredMetricaTipo("M2")}
                onMouseLeave={() => setHoveredMetricaTipo(null)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: ".3rem",
                  fontSize: ".7rem",
                  position: "relative",
                }}
              >
                <input
                  type="checkbox"
                  checked={m2}
                  disabled={!descricaoM2}
                  onChange={(e) => setM2(e.target.checked)}
                />
                <span style={{ cursor: "help" }}>M2</span>
                {hoveredMetricaTipo === "M2" && descricaoM2 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 8px)",
                      left: 0,
                      zIndex: 25,
                      width: 260,
                      padding: ".5rem .6rem",
                      borderRadius: 8,
                      background: "rgba(15, 23, 42, 0.98)",
                      color: "#fff",
                      fontSize: ".65rem",
                      lineHeight: 1.35,
                      boxShadow: "var(--shadow-md)",
                      pointerEvents: "none",
                    }}
                  >
                    {descricaoM2}
                  </div>
                )}
              </label>
              <label
                onMouseEnter={() => setHoveredMetricaTipo("M3")}
                onMouseLeave={() => setHoveredMetricaTipo(null)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: ".3rem",
                  fontSize: ".7rem",
                  position: "relative",
                }}
              >
                <input
                  type="checkbox"
                  checked={m3}
                  disabled={!descricaoM3}
                  onChange={(e) => setM3(e.target.checked)}
                />
                <span style={{ cursor: "help" }}>M3</span>
                {hoveredMetricaTipo === "M3" && descricaoM3 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 8px)",
                      left: 0,
                      zIndex: 25,
                      width: 260,
                      padding: ".5rem .6rem",
                      borderRadius: 8,
                      background: "rgba(15, 23, 42, 0.98)",
                      color: "#fff",
                      fontSize: ".65rem",
                      lineHeight: 1.35,
                      boxShadow: "var(--shadow-md)",
                      pointerEvents: "none",
                    }}
                  >
                    {descricaoM3}
                  </div>
                )}
              </label>
              <label
                onMouseEnter={() => setHoveredMetricaTipo("M4")}
                onMouseLeave={() => setHoveredMetricaTipo(null)}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: ".3rem",
                  fontSize: ".7rem",
                  position: "relative",
                }}
              >
                <input
                  type="checkbox"
                  checked={m4}
                  disabled={!descricaoM4}
                  onChange={(e) => setM4(e.target.checked)}
                />
                <span style={{ cursor: "help" }}>M4</span>
                {hoveredMetricaTipo === "M4" && descricaoM4 && (
                  <div
                    style={{
                      position: "absolute",
                      top: "calc(100% + 8px)",
                      left: 0,
                      zIndex: 25,
                      width: 260,
                      padding: ".5rem .6rem",
                      borderRadius: 8,
                      background: "rgba(15, 23, 42, 0.98)",
                      color: "#fff",
                      fontSize: ".65rem",
                      lineHeight: 1.35,
                      boxShadow: "var(--shadow-md)",
                      pointerEvents: "none",
                    }}
                  >
                    {descricaoM4}
                  </div>
                )}
              </label>
              <div
                style={{
                  flex: "1 1 200px",
                  border: "1px solid var(--color-border)",
                  borderRadius: 8,
                  padding: ".45rem .55rem",
                  background: "#fafbfc",
                }}
              >
                <div style={{ fontSize: ".62rem", opacity: 0.8 }}>
                  Cargo (automático)
                </div>
                <div style={{ fontSize: ".72rem", fontWeight: 600 }}>
                  {cargoSelecionado || "Selecione um colaborador"}
                </div>
              </div>
              <div
                style={{
                  flex: "1 1 200px",
                  border: "1px solid var(--color-border)",
                  borderRadius: 8,
                  padding: ".45rem .55rem",
                  background: "#fafbfc",
                }}
              >
                <div style={{ fontSize: ".62rem", opacity: 0.8 }}>
                  Dias úteis do mês
                </div>
                <div style={{ fontSize: ".72rem", fontWeight: 600 }}>
                  {diasUteisMesAtual}
                </div>
              </div>
            </div>

            <div
              className="flex gap-sm"
              style={{ flexWrap: "wrap", alignItems: "flex-end" }}
            >
              {podeEscolherData && (
                <div style={{ minWidth: "180px", flex: "1 1 180px" }}>
                  <label>Data da métrica</label>
                  <input
                    type="date"
                    value={dataMetrica}
                    onChange={(e) => setDataMetrica(e.target.value)}
                    max={hojeISO}
                    required
                  />
                </div>
              )}
              <div style={{ minWidth: "180px", flex: "1 1 180px" }}>
                <label>Valor diário (R$)</label>
                <input
                  disabled
                  type="number"
                  min="0"
                  step="0.01"
                  value={valorDiario}
                  onChange={(e) => setValorDiario(e.target.value)}
                  style={{ flex: "0 1 180px" }}
                  placeholder="Valor diário (R$)"
                />
              </div>
              <div style={{ minWidth: "180px", flex: "1 1 180px" }}>
                <label>Penalidade</label>
                <input
                  disabled
                  type="number"
                  min="0"
                  step="0.01"
                  value={valorPenalidade.toFixed(2)}
                  style={{ flex: "0 1 180px" }}
                  placeholder="Penalidade (R$)"
                />
              </div>
              <div style={{ minWidth: "180px", flex: "1 1 180px" }}>
                <label>Valor Total</label>
                <input
                  disabled
                  type="number"
                  min="0"
                  step="0.01"
                  value={valorTotal.toFixed(2)}
                  style={{ flex: "0 1 180px" }}
                  placeholder="Valor total (R$)"
                />
              </div>
              <input
                placeholder="Observações (justificativa da penalidade)"
                value={obs}
                onChange={(e) => setObs(e.target.value)}
                style={{ flex: "1 1 240px" }}
              />
              <div style={{ minWidth: "180px", flex: "1 1 180px" }}>
                <label>Tipo de Penalidade</label>
                <select
                  value={penalidadeSelecionada?.id || ""}
                  onChange={e => {
                    const found = penalidadesLista.find(p => p.id === e.target.value);
                    setPenalidadeSelecionada(found || null);
                  }}
                  style={{ borderColor: penalidadeSelecionada ? "#dc3545" : undefined }}
                >
                  <option value="">Sem penalidade</option>
                  {penalidadesLista.map(p => (
                    <option key={p.id} value={p.id}>
                      {p.nivel}: {Number(p.value).toFixed(2)}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <button
                className="btn"
                disabled={criar.isPending || !colaboradorId}
              >
                Salvar
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Dashboards (movido acima da tabela) */}
      {!!graficoData.length && (
        <div
          className="flex gap-md"
          style={{ flexWrap: "wrap", marginBottom: "1rem" }}
        >
          <div
            className="card"
            style={{ flex: "1 1 360px", minWidth: "300px" }}
            id="graf-dist-metricas"
          >
            <h3 style={{ marginTop: 0, fontSize: ".85rem" }}>
              Distribuição de Métricas
            </h3>
            <ResponsiveContainer width="100%" height={220}>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" label>
                  {pieData.map((p, i) => (
                    <Cell key={i} fill={cores[i]} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 10 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          <div
            className="card"
            style={{ flex: "2 1 440px", minWidth: "340px" }}
            id="graf-pontos-dia"
          >
            <h3 style={{ marginTop: 0, fontSize: ".85rem" }}>
              Pontos por Dia (Empilhado)
            </h3>
            <div className="flex gap-sm" style={{ marginBottom: ".4rem" }}>
              <button
                className="btn outline"
                style={{ padding: ".25rem .6rem", fontSize: ".6rem" }}
                onClick={() => exportar("graf-pontos-dia", "pontos-dia")}
              >
                Exportar PNG
              </button>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart
                data={graficoData}
                margin={{ left: 4, right: 8, top: 10 }}
              >
                <defs>
                  {cores.map((c, i) => (
                    <linearGradient
                      id={`gradStack${i}`}
                      key={i}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor={c} stopOpacity={0.9} />
                      <stop offset="100%" stopColor={c} stopOpacity={0.55} />
                    </linearGradient>
                  ))}
                  <filter
                    id="fShadowMetricas"
                    x="-10%"
                    y="-10%"
                    width="130%"
                    height="130%"
                  >
                    <feDropShadow
                      dx="0"
                      dy="1"
                      stdDeviation="2"
                      floodColor="#666"
                      floodOpacity="0.35"
                    />
                  </filter>
                </defs>
                <CartesianGrid strokeDasharray="2 4" stroke="#e4e6eb" />
                <XAxis dataKey="data" fontSize={10} />
                <YAxis
                  allowDecimals={false}
                  ticks={[0, 1, 2, 3, 4]}
                  fontSize={10}
                />
                <Tooltip content={<CustomTooltipStack />} />
                <Legend wrapperStyle={{ fontSize: 10 }} />
                <Bar
                  dataKey="m1"
                  stackId="a"
                  fill="url(#gradStack0)"
                  stroke={coresBordas[0]}
                  radius={[4, 4, 0, 0]}
                  filter="url(#fShadowMetricas)"
                />
                <Bar
                  dataKey="m2"
                  stackId="a"
                  fill="url(#gradStack1)"
                  stroke={coresBordas[1]}
                  radius={[4, 4, 0, 0]}
                  filter="url(#fShadowMetricas)"
                />
                <Bar
                  dataKey="m3"
                  stackId="a"
                  fill="url(#gradStack2)"
                  stroke={coresBordas[2]}
                  radius={[4, 4, 0, 0]}
                  filter="url(#fShadowMetricas)"
                />
                <Bar
                  dataKey="m4"
                  stackId="a"
                  fill="url(#gradStack3)"
                  stroke={coresBordas[3]}
                  radius={[4, 4, 0, 0]}
                  filter="url(#fShadowMetricas)"
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {resumo && (
            <div
              className="card"
              style={{ flex: "1 1 300px", minWidth: "260px" }}
            >
              <h3 style={{ marginTop: 0, fontSize: ".85rem" }}>Resumo</h3>
              <Table
                keyField="k"
                columns={[
                  {
                    key: "k",
                    header: "Indicador",
                    thStyle: { width: "60%" },
                    render: (v) => (
                      <strong style={{ fontWeight: 600 }}>{v}</strong>
                    ),
                  },
                  {
                    key: "v",
                    header: "Valor",
                    render: (v, row) => (
                      <span
                        style={{
                          fontFamily: "monospace",
                          color: row.color || "inherit",
                          fontWeight: row.color ? 600 : "normal",
                        }}
                      >
                        {v}
                      </span>
                    ),
                  },
                ]}
                data={[
                  { k: "Bônus Mensal (R$)", v: bonusMensalBase.toFixed(2) },
                  { k: "Dias (registros)", v: resumo.dias },
                  { k: "Total Pontos", v: resumo.somaPontos },
                  {
                    k: "Valor Diário (R$)",
                    v: resumo.somaValor.toFixed(2),
                  },
                  {
                    k: "Descontos Penalidades (R$)",
                    v: resumo.somaPenalidades.toFixed(2),
                    color: resumo.somaPenalidades < 0 ? "#dc2626" : "inherit",
                  },
                  {
                    k: "Valor Líquido (R$)",
                    v: resumo.valorLiquidoTotal.toFixed(2),
                    color:
                      resumo.valorLiquidoTotal > 0
                        ? "#16a34a"
                        : resumo.valorLiquidoTotal < 0
                          ? "#dc2626"
                          : "inherit",
                  },
                  (() => {
                    const falta = round2(bonusMensalBase - resumo.valorLiquidoTotal);
                    return {
                      k: falta <= 0 ? "Meta atingida (+R$)" : "Falta para meta (R$)",
                      v: Math.abs(falta).toFixed(2),
                      color: falta <= 0 ? "#16a34a" : "#f59e0b",
                    };
                  })(),
                  {
                    k: "Média Pontos/Dia",
                    v: resumo.medias.pontosDia.toFixed(2),
                  },
                  {
                    k: "Média Valor/Dia (R$)",
                    v: resumo.medias.valorDia.toFixed(2),
                  },
                ]}
              />
            </div>
          )}
        </div>
      )}

      <div className="card" style={{ marginBottom: "1rem" }}>
        <div style={{ overflowX: "auto" }}>
          <table className="table table-metricas">
            <thead>
              <tr>
                <th
                  style={{
                    width: 60,
                    position: "sticky",
                    left: 0,
                    background: "var(--color-bg, #fff)",
                    zIndex: 10,
                    boxShadow: "2px 0 4px rgba(0,0,0,0.05)",
                  }}
                />
                <th
                  style={{
                    minWidth: 280,
                    textAlign: "left",
                    position: "sticky",
                    left: 60,
                    background: "var(--color-bg, #fff)",
                    zIndex: 10,
                    boxShadow: "2px 0 4px rgba(0,0,0,0.05)",
                  }}
                >
                  DATAS:
                </th>
                {matrizMetricas.datas.map((d) => (
                  <th key={d} style={{ whiteSpace: "nowrap" }}>
                    {formatDate(d)}
                  </th>
                ))}
              </tr>
              {podeEditarMetricas && (
                <tr>
                  <th
                    style={{
                      position: "sticky",
                      left: 0,
                      background: "var(--color-bg, #fff)",
                      zIndex: 10,
                      boxShadow: "2px 0 4px rgba(0,0,0,0.05)",
                    }}
                  />
                  <th
                    style={{
                      position: "sticky",
                      left: 60,
                      background: "var(--color-bg, #fff)",
                      zIndex: 10,
                      boxShadow: "2px 0 4px rgba(0,0,0,0.05)",
                      fontSize: ".65rem",
                      fontWeight: 500,
                    }}
                  >
                    AÇÕES:
                  </th>
                  {matrizMetricas.datas.map((data) => {
                    const metricaDia = metricas.find((m) => m.data === data);
                    return (
                      <th
                        key={`edit-${data}`}
                        style={{
                          textAlign: "center",
                          padding: ".25rem",
                          position: "relative",
                          zIndex: 1,
                        }}
                      >
                        {metricaDia && (
                          <button
                            type="button"
                            onClick={() => {
                              setEditing(prepararLinhaEdicao(metricaDia));
                              setEditData(metricaDia.data);
                              setEditOpen(true);
                            }}
                            style={{
                              background: "transparent",
                              border: "none",
                              cursor: "pointer",
                              padding: ".2rem .4rem",
                              borderRadius: 4,
                              color: "var(--color-primary)",
                              display: "inline-flex",
                              alignItems: "center",
                              justifyContent: "center",
                              transition: "all 0.2s",
                              position: "relative",
                              zIndex: 1,
                            }}
                            onMouseOver={(e) => {
                              e.currentTarget.style.background =
                                "var(--color-primary-light, #e3f2fd)";
                            }}
                            onMouseOut={(e) => {
                              e.currentTarget.style.background = "transparent";
                            }}
                            title="Editar métrica"
                          >
                            <svg
                              width="14"
                              height="14"
                              viewBox="0 0 24 24"
                              fill="none"
                              stroke="currentColor"
                              strokeWidth="2"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
                            </svg>
                          </button>
                        )}
                      </th>
                    );
                  })}
                </tr>
              )}
            </thead>
            <tbody>
              {["m1", "m2", "m3", "m4"].map((key, index) => (
                <tr key={key}>
                  <th
                    style={{
                      textAlign: "center",
                      position: "sticky",
                      left: 0,
                      background: "var(--color-bg, #fff)",
                      zIndex: 9,
                      boxShadow: "2px 0 4px rgba(0,0,0,0.05)",
                    }}
                  >{`M${index + 1}`}</th>
                  <td
                    style={{
                      fontWeight: 600,
                      position: "sticky",
                      left: 60,
                      background: "var(--color-bg, #fff)",
                      zIndex: 9,
                      boxShadow: "2px 0 4px rgba(0,0,0,0.05)",
                    }}
                  >
                    {metricasNomes[key]}
                  </td>
                  {matrizMetricas.linhas[key].map((v, idx) => (
                    <td key={`${key}-${idx}`} style={{ textAlign: "center" }}>
                      {v}
                    </td>
                  ))}
                </tr>
              ))}
              <tr>
                <th
                  style={{
                    position: "sticky",
                    left: 0,
                    background: "var(--color-bg, #fff)",
                    zIndex: 9,
                    boxShadow: "2px 0 4px rgba(0,0,0,0.05)",
                    borderTop: "2px solid var(--color-border, #ddd)",
                  }}
                />
                <td
                  style={{
                    fontWeight: 700,
                    position: "sticky",
                    left: 60,
                    background: "var(--color-bg, #fff)",
                    zIndex: 9,
                    boxShadow: "2px 0 4px rgba(0,0,0,0.05)",
                    borderTop: "2px solid var(--color-border, #ddd)",
                  }}
                >
                  TOTAL
                </td>
                {matrizMetricas.totais.map((v, idx) => (
                  <td
                    key={`total-${idx}`}
                    style={{
                      textAlign: "center",
                      borderTop: "2px solid var(--color-border, #ddd)",
                    }}
                  >
                    {v}
                  </td>
                ))}
              </tr>
              <tr>
                <th
                  style={{
                    position: "sticky",
                    left: 0,
                    background: "var(--color-bg, #fff)",
                    zIndex: 9,
                    boxShadow: "2px 0 4px rgba(0,0,0,0.05)",
                  }}
                />
                <td
                  style={{
                    fontWeight: 700,
                    position: "sticky",
                    left: 60,
                    background: "var(--color-bg, #fff)",
                    zIndex: 9,
                    boxShadow: "2px 0 4px rgba(0,0,0,0.05)",
                  }}
                >
                  PENALIDADES
                </td>
                {matrizMetricas.penalidades.map((v, idx) => (
                  <td
                    key={`pen-${idx}`}
                    style={{
                      textAlign: "center",
                      color: v < 0 ? "#dc2626" : "inherit",
                    }}
                  >
                    {v}
                  </td>
                ))}
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Bloco de gráficos movido - mantém gráficos de valor e mensal */}
      {!!graficoData.length && (
        <div className="flex gap-md" style={{ flexWrap: "wrap" }}>
          <div
            className="card"
            style={{ flex: "1 1 380px", minWidth: "320px" }}
            id="graf-valor-dia"
          >
            <h3 style={{ marginTop: 0, fontSize: ".9rem" }}>
              Valor Diário (R$)
            </h3>
            <div className="flex gap-sm" style={{ marginBottom: ".4rem" }}>
              <button
                className="btn outline"
                style={{ padding: ".25rem .6rem", fontSize: ".6rem" }}
                onClick={() => exportar("graf-valor-dia", "valor-dia")}
              >
                Exportar PNG
              </button>
            </div>
            <ResponsiveContainer width="100%" height={220}>
              <BarChart data={graficoData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                <XAxis dataKey="data" fontSize={10} />
                <YAxis fontSize={10} />
                <Tooltip
                  labelFormatter={(l) => `Dia ${l}`}
                  formatter={(v) => `R$ ${Number(v).toFixed(2)}`}
                />
                <Bar
                  dataKey="valor"
                  fill="var(--color-primary)"
                  radius={[4, 4, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
          {!!agrupadoMes.length && (
            <div
              className="card"
              style={{ flex: "1 1 380px", minWidth: "320px" }}
              id="graf-mensal"
            >
              <h3 style={{ marginTop: 0, fontSize: ".9rem" }}>Média Mensal</h3>
              <div className="flex gap-sm" style={{ marginBottom: ".4rem" }}>
                <button
                  className="btn outline"
                  style={{ padding: ".25rem .6rem", fontSize: ".6rem" }}
                  onClick={() => exportar("graf-mensal", "media-mensal")}
                >
                  Exportar PNG
                </button>
              </div>
              <ResponsiveContainer width="100%" height={220}>
                <BarChart data={agrupadoMes}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
                  <XAxis dataKey="mes" fontSize={10} />
                  <YAxis fontSize={10} />
                  <Tooltip
                    formatter={(v, name) =>
                      name.includes("Valor") ? `R$ ${Number(v).toFixed(2)}` : v
                    }
                  />
                  <Bar
                    dataKey="mediaValor"
                    fill="var(--color-orange)"
                    name="Valor Médio"
                  />
                  <Bar
                    dataKey="mediaTotal"
                    fill="var(--color-blue)"
                    name="Pontos Médios"
                  />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </div>
      )}
      <Modal
        open={editOpen}
        onClose={() => setEditOpen(false)}
        title={`Editar Métrica ${editing?.data}`}
        actions={
          <>
            <button className="btn outline" onClick={() => setEditOpen(false)}>
              Cancelar
            </button>
            <button
              className="btn"
              onClick={() => {
                const payload = {
                  name: editing.name,
                  data: podeEscolherData ? editData : undefined,
                  m1: editing.m1,
                  m2: editing.m2,
                  m3: editing.m3,
                  m4: editing.m4,
                  penalidade: editing.penalidade,
                  valor_penalidade: editing.penalidade ? editing.valor_penalidade : undefined,
                  valor_diario:
                    editing.valor_diario === "" || editing.valor_diario === null
                      ? undefined
                      : Number(editing.valor_diario),
                  observacoes: editing.observacoes,
                };
                editMutation.mutate({ id: editing.id, payload });
              }}
              disabled={editMutation.isPending}
            >
              Salvar
            </button>
          </>
        }
      >
        {editing && (
          <div className="flex gap-sm" style={{ flexWrap: "wrap" }}>
            <div style={{ flex: "1 1 100%", marginBottom: ".5rem" }}>
              <label style={{ fontSize: ".6rem", fontWeight: 600 }}>
                Metricas
              </label>
            </div>
            {podeEscolherData && (
              <div style={{ flex: "1 1 100%" }}>
                <label style={{ fontSize: ".6rem", fontWeight: 600 }}>
                  Data
                </label>
                <input
                  type="date"
                  value={editData}
                  onChange={(e) => {
                    const nextData = e.target.value;
                    setEditData(nextData);
                    setEditing((ed) => {
                      const atualizado = { ...ed, data: nextData };
                      const valorRecalculado = calcularValorDiarioPorData(
                        nextData,
                        atualizado.m1,
                        atualizado.m2,
                        atualizado.m3,
                        atualizado.m4,
                      );
                      return {
                        ...atualizado,
                        valor_diario: valorRecalculado.toFixed(2),
                      };
                    });
                  }}
                  max={hojeISO}
                  style={{ width: "100%", fontSize: ".75rem" }}
                />
              </div>
            )}
            {[1, 2, 3, 4].map((i) => (
              <label
                key={i}
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: ".3rem",
                  fontSize: ".75rem",
                }}
              >
                <input
                  type="checkbox"
                  checked={!!editing["m" + i]}
                  onChange={(e) => {
                    const checkedValue = e.target.checked ? 1 : 0;
                    setEditing((ed) => {
                      const atualizado = {
                        ...ed,
                        ["m" + i]: checkedValue,
                      };
                      const valorRecalculado = calcularValorDiarioPorData(
                        atualizado.data,
                        atualizado.m1,
                        atualizado.m2,
                        atualizado.m3,
                        atualizado.m4,
                      );
                      return {
                        ...atualizado,
                        valor_diario: valorRecalculado.toFixed(2),
                      };
                    });
                  }}
                />
                M{i}
              </label>
            ))}
            <div style={{ flex: "1 1 100%" }}>
              <label style={{ fontSize: ".6rem", fontWeight: 600 }}>Penalidade</label>
              <select
                value={editing.penalidade ? String(editing.valor_penalidade ?? "") : ""}
                onChange={e => {
                  if (!e.target.value) {
                    setEditing(ed => ({ ...ed, penalidade: 0, valor_penalidade: 0 }));
                  } else {
                    setEditing(ed => ({ ...ed, penalidade: 1, valor_penalidade: Number(e.target.value) }));
                  }
                }}
                style={{ width: "100%", borderColor: editing.penalidade ? "#dc3545" : undefined }}
              >
                <option value="">Sem penalidade</option>
                {penalidadesLista.map(p => (
                  <option key={p.id} value={String(p.value)}>
                    {p.nivel} ({p.type}): {Number(p.value).toFixed(2)}
                  </option>
                ))}
              </select>
            </div>
            <div style={{ flex: "1 1 100%" }}>
              <label style={{ fontSize: ".6rem", fontWeight: 600 }}>Nome</label>
              <input
                disabled
                value={editing.name || ""}
                readOnly
                style={{ width: "100%", fontSize: ".75rem" }}
              />
            </div>
            <div style={{ flex: "1 1 100%" }}>
              <label style={{ fontSize: ".6rem", fontWeight: 600 }}>
                Valor diário (R$)
              </label>
              <input
                disabled
                type="number"
                min="0"
                step="0.01"
                value={editing.valor_diario ?? ""}
                onChange={(e) =>
                  setEditing((ed) => ({ ...ed, valor_diario: e.target.value }))
                }
                style={{ width: "100%", fontSize: ".75rem" }}
              />
            </div>
            <div style={{ flex: "1 1 100%" }}>
              <label style={{ fontSize: ".6rem", fontWeight: 600 }}>
                Observações
              </label>
              <textarea
                value={editing.observacoes || ""}
                onChange={(e) =>
                  setEditing((ed) => ({ ...ed, observacoes: e.target.value }))
                }
                style={{ width: "100%", minHeight: "80px" }}
              />
            </div>
          </div>
        )}
        {editMutation.isPending && (
          <p className="muted" style={{ fontSize: ".65rem" }}>
            Salvando...
          </p>
        )}
      </Modal>

      {/* Modal Gerenciar Penalidades */}
      <Modal
        open={penModalOpen}
        onClose={() => {
          setPenModalOpen(false);
          setPenEditing(null);
          setPenForm({ nivel: "", type: "", value: "" });
          setPenDeleteConfirm(null);
        }}
        title="Gerenciar Penalidades"
        actions={
          <button
            className="btn outline"
            onClick={() => {
              setPenModalOpen(false);
              setPenEditing(null);
              setPenForm({ nivel: "", type: "", value: "" });
              setPenDeleteConfirm(null);
            }}
          >
            Fechar
          </button>
        }
      >
        {/* Formulário criar / editar */}
        <form
          onSubmit={(e) => {
            e.preventDefault();
            const dados = {
              nivel: penForm.nivel,
              type: penForm.type,
              value: Number(penForm.value),
            };
            if (penEditing) {
              atualizarPenalidade.mutate({ id: penEditing.id, dados });
            } else {
              criarPenalidade.mutate(dados);
            }
          }}
          style={{ display: "flex", flexWrap: "wrap", gap: ".5rem", marginBottom: "1rem", alignItems: "flex-end" }}
        >
          <div style={{ flex: "1 1 140px" }}>
            <label style={{ fontSize: ".6rem", fontWeight: 600 }}>Nível</label>
            <input
              required
              placeholder="Ex: Leve, Grave"
              value={penForm.nivel}
              onChange={(e) => setPenForm((f) => ({ ...f, nivel: e.target.value }))}
              style={{ width: "100%", fontSize: ".75rem" }}
            />
          </div>
          <div style={{ flex: "1 1 140px" }}>
            <label style={{ fontSize: ".6rem", fontWeight: 600 }}>Tipo</label>
            <input
              required
              placeholder="Ex: Falta, Atraso"
              value={penForm.type}
              onChange={(e) => setPenForm((f) => ({ ...f, type: e.target.value }))}
              style={{ width: "100%", fontSize: ".75rem" }}
            />
          </div>
          <div style={{ flex: "1 1 120px" }}>
            <label style={{ fontSize: ".6rem", fontWeight: 600 }}>Valor (R$)</label>
            <input
              required
              type="number"
              step="0.01"
              max="-0.01"
              placeholder="Ex: -50.00"
              value={penForm.value}
              onChange={(e) => setPenForm((f) => ({ ...f, value: e.target.value }))}
              style={{ width: "100%", fontSize: ".75rem", borderColor: penForm.value !== "" && Number(penForm.value) >= 0 ? "#dc2626" : undefined }}
            />
            {penForm.value !== "" && Number(penForm.value) >= 0 && (
              <span style={{ fontSize: ".6rem", color: "#dc2626" }}>O valor deve ser negativo</span>
            )}
          </div>
          <div style={{ display: "flex", gap: ".4rem" }}>
            <button
              className="btn"
              type="submit"
              disabled={criarPenalidade.isPending || atualizarPenalidade.isPending}
              style={{ fontSize: ".72rem" }}
            >
              {penEditing ? "Atualizar" : "Criar"}
            </button>
            {penEditing && (
              <button
                type="button"
                className="btn outline"
                onClick={() => {
                  setPenEditing(null);
                  setPenForm({ nivel: "", type: "", value: "" });
                }}
                style={{ fontSize: ".72rem" }}
              >
                Cancelar
              </button>
            )}
          </div>
        </form>

        {/* Confirmação de exclusão */}
        {penDeleteConfirm && (
          <div
            style={{
              background: "#fff5f5",
              border: "1px solid #fca5a5",
              borderRadius: 6,
              padding: ".5rem .75rem",
              marginBottom: ".75rem",
              fontSize: ".72rem",
              display: "flex",
              alignItems: "center",
              gap: ".75rem",
              flexWrap: "wrap",
            }}
          >
            <span>
              Confirmar exclusão de <strong>{penDeleteConfirm.nivel} ({penDeleteConfirm.type})</strong>?
            </span>
            <div style={{ display: "flex", gap: ".4rem", marginLeft: "auto" }}>
              <button
                className="btn"
                style={{ background: "#dc2626", border: "none", fontSize: ".7rem" }}
                disabled={deletarPenalidade.isPending}
                onClick={() => deletarPenalidade.mutate(penDeleteConfirm.id)}
              >
                Confirmar
              </button>
              <button
                className="btn outline"
                style={{ fontSize: ".7rem" }}
                onClick={() => setPenDeleteConfirm(null)}
              >
                Cancelar
              </button>
            </div>
          </div>
        )}

        {/* Tabela de penalidades */}
        {penalidadesLista.length === 0 ? (
          <p className="muted" style={{ fontSize: ".72rem" }}>Nenhuma penalidade cadastrada.</p>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table className="table" style={{ fontSize: ".72rem", width: "100%" }}>
              <thead>
                <tr>
                  <th>Nível</th>
                  <th>Tipo</th>
                  <th>Valor (R$)</th>
                  <th style={{ width: 120 }}></th>
                </tr>
              </thead>
              <tbody>
                {penalidadesLista.map((p) => (
                  <tr key={p.id} style={{ background: penEditing?.id === p.id ? "var(--color-primary-light, #e3f2fd)" : undefined }}>
                    <td>{p.nivel}</td>
                    <td>{p.type}</td>
                    <td style={{ color: Number(p.value) < 0 ? "#dc2626" : "inherit", fontWeight: 600 }}>
                      {Number(p.value).toFixed(2)}
                    </td>
                    <td>
                      <div style={{ display: "flex", gap: ".3rem" }}>
                        <button
                          className="btn outline"
                          style={{ padding: ".2rem .45rem", fontSize: ".65rem" }}
                          onClick={() => {
                            setPenEditing(p);
                            setPenForm({ nivel: p.nivel, type: p.type, value: String(p.value) });
                            setPenDeleteConfirm(null);
                          }}
                        >
                          Editar
                        </button>
                        <button
                          className="btn"
                          style={{ padding: ".2rem .45rem", fontSize: ".65rem", background: "#dc2626", border: "none" }}
                          onClick={() => {
                            setPenDeleteConfirm(p);
                            if (penEditing?.id === p.id) {
                              setPenEditing(null);
                              setPenForm({ nivel: "", type: "", value: "" });
                            }
                          }}
                        >
                          Deletar
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Modal>
    </div>
  );
}
